import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Icon, Button, Tooltip } from '@scuf/common';
import { DataTable } from '@scuf/datatable';
import DeviceActions from '../Redux/DeviceRedux';
import ModalActions from '../Redux/ModalRedux';
import { useHistory } from 'react-router-dom';
import { currentDeviceSelector } from '../Selectors/DeviceSelector';
import { useTranslate } from 'react-translate';
import styled from 'styled-components/macro';
import DeviceSite from 'Components/AssetManagementBase/components/device-assigned-site';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationUsageExportApi } from 'Services';
import { toast } from 'react-toastify';
import { ErrorToast, SuccessToast } from 'Themes/ScufStyledComponents';
import { getStartDate, getEndDate } from 'Utils/MomentHelpers';
import UpdatesActions from 'Redux/UpdatesRedux';
import useDetailsDownload from './DashboardDetails/components/Export/dashboard-details-export.hooks';
import { getStartDateByUnitTime, substract, today,substractStartDayCurrently } from 'Utils/MomentHelpers';
import { defaultFlags } from 'Utils/launch-darkly-flags';
import { createSelector } from 'reselect'
import { HealthDanger, HealthWarning, AgeWarning, CycleWarning, CycleDanger, AgeDanger  } from './DashboardCard/DashboardCard.styles'

const ExportWrap = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 0 1rem 0 0;
  & .h-icon {
    margin-right: 0.3rem;
  }
  a {
    display: flex;
    justify-content: center;
    align-items: center;
    color: ${(props) => (props.theme.black ? '#fff' : '#303030')} !important;
    font-weight: 400;
  }
  margin-top: ${(props) => (props.heigthDrill ? '-1.7rem' : '0rem')};
  margin-bottom: ${(props) => (props.heigthDrill ? '0rem' : '1rem')};
  @media (max-width: 768px) {
    margin: 0.5rem 0rem;
  }
`;
const StyledDataTable = styled(DataTable)`
  margin-top: ${(props) => (props.heigthDrill ? '2rem' : '0rem')};
  @media (max-width: 768px) {
    margin-top: 0rem;
  }
  & .p-datatable-wrapper {
    height: ${(props) => (props.columnsInData ? '26rem' : '4.5rem')};
  }
  & .cell-data-wrap {
    padding: 0 0 0 1.5rem !important;
    font-size: 0.875rem;
    @media (max-width: 768px) {
      padding: 0 !important;
    }
  }
  & .p-datatable-row {
    height: 32px !important;
  }
  & .p-checkbox-box {
    max-width: 1rem !important;
    max-height: 1rem !important;
  }
  & .p-checkbox-icon {
    font-size: 0.5rem !important;
    top: -3px !important;
  }
  & .p-datatable-header {
    max-height: 3rem;
    background-color: #fff !important;
  }
  & .input-wrap {
    margin: 0.25rem 0 0.25rem 0;
  }
  & .total-count {
    font-size: 0.875rem;
    font-weight: 400;
  }
  & .ui.forge-datatable-wrap .select-all-checkbox {
    z-index: ${(props) => (props.heigthDrill ? '0' : '1')} !important;
  }
`;
const siteRenderer = ({ field, value, rowData }) => {
  return <DeviceSite cellData={value} />;
};
const AssetManagementTable = ({
  flags,
  siteId,
  data,
  columns,
  totalRows,
  totalLengthData = 100,
  pageSize = 100,
  isLoading = false,
  nextPageApi = null,
  onSearch,
  startDate,
  endDate,
  reportingDays = 0,
  bottomBatteryPercentage = 50,
  cycleCountTop = 500,
  agingMonthsTop = 30,
  cycleCount = 300,
  expanderTemplate,
  isDetailsTable,
  deviceNameDetail,
  fileName,
  theme,
  heigthDrill,
  expandedRows,
  columnsInData = false,
  rowExpansionTemplate,
  loading,
  onSearchDetails = true,
  noSelectionMode,
  disableRowClick,
  onRowClick,
  isTagManagement,
  onEditDevicesTags,
  hoursOffline,
  batteryPercentage = 75,
  cycleCountAbove,
  agingMonths,
  month,
  orgUnitId,
  dockingTime,
  hoursOfUsePerDay,
  handleDockingTime,
  deviceType,
  exportApi = () => {},
  filterKey,
  setFilterKey,
  isApplicationReportExportsButtonDisable = false,
}) => {
  const { downloadTableData } = useDetailsDownload()
  const t = useTranslate('AssetManagementTable');
  const history = useHistory();
  const dispatch = useDispatch();
  const devices = useSelector((state) => currentDeviceSelector(state));
  let filteredColumns = columns;
  
  // Helper to insert columns with custom renderers
  const insertBatteryColumns = (columns, renderers) => {
    const cols = [...columns];
    if (renderers.batteryAge) {
      cols.splice(1, 0, renderers.batteryAge);
    }
    if (renderers.cycleCount) {
      cols.splice(1, 0, renderers.cycleCount);
    }
    if (renderers.batteryPercentage) {
      cols.splice(1, 0, renderers.batteryPercentage);
    }

    return cols;
  };

  if (fileName === 'batteriesAbove' || fileName === 'batteriesBelow') {
    const isAbove = fileName === 'batteriesAbove';
    const HealthComp = isAbove ? HealthWarning : HealthDanger;
    const CycleComp = isAbove ? CycleWarning : CycleDanger;
    const AgeComp = isAbove ? AgeWarning : AgeDanger;

    const batteryRenderers = {
      batteryPercentage: {
        field: 'batteryPercentage',
        translation: 'AssetManagementTable_lowHealth',
        render: ({ value }) => <HealthComp value={value}>{value} %</HealthComp>,
        initialWidth: 120,
      },
      cycleCount: {
        field: 'cycleCount',
        translation: 'AssetManagementTable_cycles',
        render: ({ value }) => <CycleComp value={value}>{value}</CycleComp>,
        initialWidth: 120,
      },
      batteryAge: {
        field: 'batteryAge',
        translation: 'AssetManagementTable_age',
        render: ({ value }) => <AgeComp value={value}>{value}</AgeComp>,
        initialWidth: 120,
      },
    };

    if (Array.isArray(devices.batteriesProperties)) {
      if (devices.batteriesProperties.length === 0) {
        filteredColumns = insertBatteryColumns(columns, batteryRenderers);
      } else {
        const renderersToInsert = {};
        if (devices.batteriesProperties.includes('batteryPercentage')) {
          renderersToInsert.batteryPercentage = batteryRenderers.batteryPercentage;
        }
        if (devices.batteriesProperties.includes('cycleCount')) {
          renderersToInsert.cycleCount = batteryRenderers.cycleCount;
        }
        if (devices.batteriesProperties.includes('batteryAge')) {
          renderersToInsert.batteryAge = batteryRenderers.batteryAge;
        }
        filteredColumns = insertBatteryColumns(columns, renderersToInsert);
      }
    }
  }
  
  const state = useSelector((state) => state);
  const lastOnlineHours = hoursOffline || 72;
  const language = useSelector(({ user }) => user.getIn(['language'], []))
  const selectHardUsesOcurred = (state) => 
      state.dashboard.getIn(['hardUseDevicesCard'], {});
// (No replacement lines needed; the commented-out code is removed entirely.)
  const selectDockingTimeValue = createSelector(
    [selectHardUsesOcurred],
    (hardUsesOcurred) => {
        if (hardUsesOcurred && typeof hardUsesOcurred.getIn === 'function') {
            return hardUsesOcurred.getIn(['dockingTime'], true);
        } else {
            console.error("Expected hardUsesOcurred to be an Immutable Map, but got:", hardUsesOcurred);
            return null; // Handle the invalid case appropriately
        }
    }
);
  const harddockingTime = useSelector(selectDockingTimeValue);
  const [dataTableData, setDataTableData] = useState(data);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState(null);
  const [selectedRow, setSelectedRow] = useState([]);
  const [selectedAll, setSelectedAll] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(pageSize * 2);
  const searchCondition = fileName === 'applicationUsage' || fileName === 'networkIO'
    ? isDetailsTable ? t('Search_Details') : t('Search_Table')
    : isDetailsTable ? t('Search_Details') : t('Search_Table_For')
  const setLoading = (state) => dispatch(UpdatesActions.updatesSetLoading(state));
  const dateSelected = useSelector(state => state.dashboard.dateSelected);
  
  useEffect(() => {
    if (data.length === totalRows) {
      setDataTableData(data);
    } else {
      slicedData({ first, rows });
    }
  }, [data]);

  const getExportApplicationUsage = async () => {
    const FormatedStartDate = getStartDate(startDate);
    const FormatedEndDate = getEndDate(endDate);
    setLoading(true);
    const resp = await ApplicationUsageExportApi.getExportFile(siteId, FormatedStartDate, FormatedEndDate, fileName, language);
    if (resp.ok) {
      const path = '/reports/downloads';
      toast(<SuccessToast message={t('ExportToastSuccess')} detailsClick={() => history.push(path)} detailsText={t('ExportTextDetails')} />);
    } else {
      toast(<ErrorToast message={t('Export_Failed')} />);
    }
    setLoading(false);
  };

  const getExportApplicationUsageByName = async () => {
    if (isDetailsTable) {
      const FormatedStartDate = getStartDate(startDate);
      const FormatedEndDate = getEndDate(endDate);
      setLoading(true);
      const resp = await ApplicationUsageExportApi.getExportFileDetailByName(
        siteId,
        deviceNameDetail,
        FormatedStartDate,
        FormatedEndDate,
        fileName,
        language
      );
      if (resp.ok) {
        const path = '/reports/downloads';
        toast(<SuccessToast message={t('ExportToastSuccess')} detailsClick={() => history.push(path)} detailsText={t('ExportTextDetails')} />);
      } else {
        toast(<ErrorToast message={t('Export_Failed')} />);
      }
      setLoading(false);
    }
  };

  const downloadProvisonResults = () => {
    setLoading(true);
    const csv = 'data:text/csv;charset=utf-8,' + encodeURI(parseProvisionResultsToCsvFormat());
    const link = document.createElement('a');
    link.href = csv;
    link.download = `${fileName}.csv`;
    document.body.appendChild(link);
    link.click();
    setLoading(false);
  };

  const getOfflineDevices = async () => {
    const devicePath = `/assets/management/all?sites%5B0%5D=${siteId}&includeChildSites=true&hours=${lastOnlineHours}`;
    history.push(devicePath);
  };

  const onExport = () => {
    const exportParameters = {};
    setExportParameters(exportParameters);
    const FormatedStartDate = getStartDate(startDate);
    const FormatedEndDate = getEndDate(endDate);
    const filteredColumnsForExportBatteriesProperties = devices.batteriesProperties.map(property => property);
    downloadTableData({ siteId, startDate: FormatedStartDate, endDate: FormatedEndDate, hours: lastOnlineHours, tableId: fileName, selectedRow, language, reportingDays, exportParameters, columns: filteredColumnsForExportBatteriesProperties, tableParams: {}, t });
  }
  const setExportParameters = (exportParameters) => {
    if (fileName && fileName.startsWith('DeviceByUsageLevels-')) {
    startDate = getStartDateByUnitTime('year', 'l');
    exportParameters.orgId = orgUnitId
    exportParameters.month = month
    exportParameters.dockingTime = dockingTime
    exportParameters.harddockingTime = dockingTime
  }
  else{
    switch (fileName) {
      case 'typesOfModelsModal':
      case 'devicesBySiteModal':
        startDate = getStartDateByUnitTime('year', 'l')
        break;
      case 'newBatteriesList':
        startDate = getStartDateByUnitTime('month', 'l')
        break;
      case 'offlineDevicesByModelModal':
      case 'offlineDevicesBySiteModal':
        startDate = getStartDateByUnitTime('year', 'l')
        reportingDays = lastOnlineHours
        break;
      case 'batteryList':
        startDate = substractStartDayCurrently(today(), 1, 'days', 'l');
        exportParameters.cycleCountAbove=cycleCountAbove;
        break;
      case 'batteryAgeList':
        startDate = substractStartDayCurrently(today(), 1, 'days', 'l');
        exportParameters.agingMonths=agingMonths;
        break;
      case 'printersFirmwareModal':
        startDate = substractStartDayCurrently(today() , 6, 'days', 'l');
        exportParameters.deviceType = 'printer';
        break;
      case 'batteryListLowHealth':
        startDate = substractStartDayCurrently(today(), 1, 'months', 'l')
        exportParameters.batteryPercentage = batteryPercentage
        break;
      case 'batteriesAbove':
        startDate = substractStartDayCurrently(today(), 1, 'days', 'l');
        if (Array.isArray(devices.batteriesProperties) && devices.batteriesProperties.length === 0) {
              exportParameters.batteryPercentage = batteryPercentage
              exportParameters.agingMonths = agingMonthsTop
              exportParameters.cycleCount = cycleCount
              exportParameters.minimumBatteryPercentage = bottomBatteryPercentage
              exportParameters.cycleCountTop = cycleCountTop
              exportParameters.agingMonthsTop = agingMonths
        } else {
            if (devices.batteriesProperties.includes('batteryPercentage')) {
              exportParameters.batteryPercentage = batteryPercentage
              exportParameters.minimumBatteryPercentage = bottomBatteryPercentage
            }
            if (devices.batteriesProperties.includes('cycleCount')) {
              exportParameters.cycleCount = cycleCount
              exportParameters.cycleCountTop = cycleCountTop
            }
            if (devices.batteriesProperties.includes('batteryAge')) {
              exportParameters.agingMonths = agingMonthsTop
              exportParameters.agingMonthsTop = agingMonths
            }
        }

        break;
      case 'batteriesBelow':
        startDate = substractStartDayCurrently(today(), 1, 'days', 'l');
        if (Array.isArray(devices.batteriesProperties) && devices.batteriesProperties.length === 0) {
          exportParameters.batteryPercentage = bottomBatteryPercentage;
          exportParameters.agingMonths = agingMonths;
          exportParameters.cycleCount = cycleCountTop;
        } else {
            if (devices.batteriesProperties.includes('batteryPercentage')) {
            exportParameters.batteryPercentage = bottomBatteryPercentage;
            }
            if (devices.batteriesProperties.includes('cycleCount')) {
            exportParameters.cycleCount = cycleCountTop;
            }
            if (devices.batteriesProperties.includes('batteryAge')) {
            exportParameters.agingMonths = agingMonths;
            }
        }
        break;
      case 'deviceUsageLevels':
        startDate = getStartDateByUnitTime('year', 'l');
        exportParameters.month = month
        exportParameters.dockingTime = dockingTime
        exportParameters.harddockingTime = dockingTime
        break;
      case 'deviceHardUsageLevels':
        startDate = dateSelected;
        endDate = dateSelected;
        exportParameters.minHours = hoursOfUsePerDay
        exportParameters.harddockingTime = harddockingTime
        break;
      case 'Devices with 8+ hours of use per day':
        startDate = dateSelected;
        endDate = dateSelected;
        exportParameters.minHours = hoursOfUsePerDay
        exportParameters.orgUnitId = data.orgUnitId
        exportParameters.harddockingTime = harddockingTime
        break;
      default:
        startDate = today();
        reportingDays = 0;
      }
    }
  }

  const parseProvisionResultsToCsvFormat = () => {
    const dataToExport = selectedRow.length === 0 ? data : selectedRow;
    const header = filteredColumns.map((value) => t(value.translation));
    let csvText = header.join(',') + '\n';
    dataToExport.map((row) => {
      csvText +=
        filteredColumns
          .map((column) =>
            typeof row[column.field] === 'string' && row[column.field]?.includes(',')
              ? `"${row[column.field]}"`
              : row[column.field]
          )
          .join(',') + '\n';
    });
    return csvText;
  };

  const redirectToDetails = ({ data }) => {
    dispatch(DeviceActions.deviceCurrent(data));
    const path = data.currentDevice ? `/assets/management/devices/${data.currentDevice}` : '/assets/management/all';
    history.push(path);
    dispatch(ModalActions.closeModal());
  };
  const nextPageApiCall = (currentPage, pages) => {
    if (currentPage >= pages) {
      setIsDone(true);
    }
    if (startDate && endDate) {
      isDetailsTable
        ? nextPageApi(deviceNameDetail, siteId, {
            page: currentPage,
            pagesize: pageSize,
            startDate: getStartDate(startDate),
            endDate: getEndDate(endDate),
          })
        : nextPageApi(siteId, {
            page: currentPage,
            pagesize: pageSize,
            startDate: getStartDate(startDate),
            endDate: getEndDate(endDate),
            filterKey: filterKey,
          });
    } else {
      isDetailsTable
        ? nextPageApi(deviceNameDetail, siteId, {
            page: currentPage,
            pagesize: pageSize,
            startDate: new Date(new Date().setUTCHours(0, 0, 0)).toISOString(),
            endDate: new Date(new Date().setUTCHours(23, 59, 59)).toISOString(),
          })
        : nextPageApi(siteId, {
            page: currentPage,
            pagesize: pageSize,
            startDate: new Date(new Date().setUTCHours(0, 0, 0)).toISOString(),
            endDate: new Date(new Date().setUTCHours(23, 59, 59)).toISOString(),
            filterKey: filterKey,
          });
    }
  };
  const slicedData = ({ first, rows }) => {
    let last = first + rows;
    if (first + rows > totalRows) {
      last = totalRows;
    }
    setDataTableData([...data].slice(first, last));
  };
  const handleScroll = ({ first, rows }) => {
    const pages = Math.ceil(totalRows / (pageSize || 1) || 1);
    const currentRows = data.length || 0;
    const currentPage = (Math.ceil(currentRows / pageSize || 1) || 1) + 1;
    setFirst(first);
    setRows(rows);
    if (currentRows > 20 && currentPage <= pages) {
      nextPageApiCall(currentPage, pages);
    }
  };
  return (
    <>
      <ExportWrap theme={theme} heigthDrill={heigthDrill}>
        {isDetailsTable ? (
          <Button
            icon={<Icon root="building" name="export" size="medium" />}
            type="link"
            content={t('Export')}
            onClick={getExportApplicationUsageByName}
          />
        ) : fileName === 'applicationUsage' || fileName === 'networkIO' ? (
          <Button
            icon={<Icon root="building" name="export" size="medium" />}
            type="link"
            content={t('Export')}
            onClick={getExportApplicationUsage}
          />
        ) : ((fileName === 'devicesBySiteModal' && flags.devicesBySiteV2Export) 
            || (fileName === 'typesOfModelsModal' && flags.devicesByModelV2Export)
            || (fileName === 'newBatteriesList' && flags.newBatteriesCurrentMonthV2Export)
            || (fileName === 'offlineDevicesByModelModal' && flags.offlineDevicesByModelV2Export)
            || (fileName === 'batteryListLowHealth' && flags.batteriesLowHealthV2Export) 
            || (fileName === 'batteryList' && flags.batteriesCycleCountV2Export)
            || (fileName === 'batteryAgeList' && flags.agingBatteriesV2Export) 
            || (fileName === 'offlineDevicesBySiteModal' && flags.offlineDevicesBySiteV2Export)
            || (fileName === 'batteriesAbove' && flags.batteriesReplaceSoonV2Export)
            || (fileName === 'batteriesBelow' && flags.batteriesReplaceNowV2Export)
            || (fileName === 'deviceHardUsageLevels' && flags.hardUseDevicesV2Export)
            || (fileName === 'Devices with 8+ hours of use per day' && flags.hardUseDevicesV2Export)
            || (fileName === 'deviceUsageLevels' && flags.devicesByUsageLevelsV2Export)
            || (fileName && fileName.startsWith('DeviceByUsageLevels-') && flags.devicesByUsageLevelsV2Export)
            || (fileName === 'printersFirmwareModal' && flags.printersFirmwareVersionV2Export) ? ( <>
            {fileName === 'offlineDevicesByModelModal' || fileName === 'offlineDevicesBySiteModal' ? (
              <Button
                type="link"
                content={t('Devices')}
                onClick={getOfflineDevices}
              />) : null
            } 
          <Button
            icon={<Icon root="building" name="export" size="medium" />}
            type="link"
            content={t('Export')}
            onClick = {onExport}
          /></>
        ) : (
          <Button
            icon={<Icon root="building" name="export" size="medium" />}
            type="link"
            content={t('Export')}
            onClick={downloadProvisonResults}
            disabled={isApplicationReportExportsButtonDisable}
          />
        ))}
      </ExportWrap>
      <div>
        <StyledDataTable
          data={dataTableData}
          totalRecords={totalRows}
          lazy={nextPageApi ? (totalRows < 100 ? false : true) : false}
          virtualScroll={nextPageApi ? (totalRows < 100 ? false : true) : false}
          onVirtualScroll={nextPageApi ? (isDone ? slicedData : handleScroll) : null}
          rows={nextPageApi ? totalLengthData : data.length}
          virtualRowHeight={47}
          scrollable={true}
          scrollHeight={!isDetailsTable ? '55vh' : '30vh'}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={() => {}}
          heigthDrill={heigthDrill}
          expandedRows={expandedRows}
          columnsInData={columnsInData}
          rowExpansionTemplate={(rowData) => rowExpansionTemplate(rowData, flags, dockingTime, hoursOfUsePerDay)}
          loading={loading}
          search={true}
          selection={noSelectionMode ? null : selectedRow}
          metaKeySelection={false}
          selectionMode={noSelectionMode ? null : !data.length ? 'single' : 'multiple'}
          onRowDoubleClick={disableRowClick ? null : onRowClick || redirectToDetails}
          onSelectAll={(e) => setSelectedAll(e)}
          onSelectionChange={(e) => setSelectedRow(e)}
          searchPlaceholder={searchCondition}
        >
          {isTagManagement ? (
            <StyledDataTable.ActionBar>
              <StyledDataTable.ActionBar.Item
                icon={<Icon root="common" name="tag" size="small" />}
                content={t('AssetManagementTable_TagManagement')}
                onClick={() => onEditDevicesTags(selectedRow.map((a) => a.details))}
              />
            </StyledDataTable.ActionBar>
          ) : null}
          {filteredColumns.map((key) => {
              const defaultRenderer = key.render;
                let customRenderer = defaultRenderer;
                if (key.field === 'batteryPercentage' && (fileName === 'batteriesBelow' || fileName === 'batteriesAbove')) {
                    customRenderer = (value) => {
                        const HealthComponent = fileName === 'batteriesBelow' ? HealthDanger : HealthWarning;
                        return value?.rowData?.cycleCount < 5
                            ? t('MinimumChargeCyclesNotMet')
                            : <HealthComponent value={value?.value}>{`${value?.value} %`}</HealthComponent>;
                    };
                }
            if (key.field === 'sitePath') {
              return (
                <StyledDataTable.Column
                  initialWidth={key.initialWidth}
                  key={key.field}
                  field={key.field}
                  renderer={siteRenderer}
                  header={<Tooltip element={<div>{(t(key.translation))}</div>} content={t(key.translation).toUpperCase()} position="top right" />  || ''}
                  // sortable={key.field !== 'collapse' ? true : false}
                />
              );
            } else {
              return (
                <StyledDataTable.Column
                  initialWidth={key.initialWidth}
                  key={key.field}
                  field={key.field}
                  renderer={key.field !== 'collapse' ? customRenderer  : expanderTemplate}
                  header={<Tooltip element={<div>{(t(key.translation)).toUpperCase()}</div>} content={t(key.translation).toUpperCase()} position="top right" /> || ''}
                  // sortable={key.field !== 'collapse' ? true : false}
                />
              );
            }
          })}
        </StyledDataTable>
      </div>
    </>
  );
};
export default AssetManagementTable;

